import { ORPCError } from "@orpc/client";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import {
  type ApiOutputs,
  assistedNoteConflictSchema,
  combinedNoteEditInstruction,
  type NoteEditOrigin,
  noteEditProposalConflictSchema,
  noteEditProposalInputSchema,
  noteEditProposalSchema,
  visitNoteSchema,
  visitNoteUpdateSchema,
} from "@rosc/contracts";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { referenceClient } from "@/lib/orpc/client";
import { type PreChartLabs, preChartLabsFor } from "@/lib/pre-chart-labs";
import {
  getVisitScope,
  refreshVisitReads,
  useVisitScope,
  type VisitScope,
} from "@/lib/visit-queries";
import { useAuthStore } from "@/store/auth.store";

export type NoteProposal = ApiOutputs["createNoteEditProposal"];
export type NoteAssistantTarget = NoteProposal["target"];
type Lifetime = { scope: VisitScope | null; source: string };
type State = {
  lifetime: Lifetime;
  result: NoteProposal | null;
  phase: "proposing" | "ready" | "applying" | "proposal-error";
};
// A proposal being refined before it is applied: the next instruction runs
// on its text, and the result replaces it as one proposal over the saved
// note (`instructions`: the requests it already contains, oldest first).
export type NoteProposalRefine = {
  proposal: NoteProposal;
  instructions: readonly string[];
};
export type ProposeOutcome = "ready" | "stale" | "error";
// Pre-chart drafts: how attached files are added. "spread": into the
// template's headings (an earlier consult note summarized under the
// history heading); "summary": a short summary under `heading`; "labs": a
// lab report's values (`labs`: all, abnormal or the picked tests) under
// `heading`.
export interface PreChartAddRequest {
  mode: "spread" | "summary" | "labs";
  heading?: string;
  labs?: PreChartLabs;
}
const orpc = createTanstackQueryUtils(referenceClient);
// An applied edit waits at most this long for the note to show it.
export const NOTE_REFRESH_WAIT_MS = 5000;
const savedSchema = visitNoteSchema.pick({ public_id: true });
export function useNoteAssistant(
  target: NoteAssistantTarget,
  instruction: string,
  // Visit sources (uploaded labs, reports, notes) the edit may use.
  sourceIds: readonly number[] = [],
  // "transcript-edit": every clinical fact change needs confirmation.
  origin?: NoteEditOrigin,
  refine?: NoteProposalRefine,
  // A pre-chart draft: its "Pre-chart labs" choice goes with each request.
  preChartDraft = false,
  // Pre-chart drafts: how the files are added ("Spread into sections", or
  // "Summarize and insert" under a heading).
  preChartAdd?: PreChartAddRequest,
) {
  const scope = useVisitScope();
  const source = JSON.stringify([
    target.visitPublicId,
    target.notePublicId,
    instruction,
    ...(sourceIds.length ? [sourceIds] : []),
    ...(origin ? [origin] : []),
    ...(preChartAdd
      ? [
          preChartAdd.mode,
          preChartAdd.heading ?? "",
          ...(preChartAdd.labs ? [preChartAdd.labs] : []),
        ]
      : []),
    ...(refine
      ? [
          refine.instructions,
          refine.proposal.baseVersion,
          refine.proposal.proposed,
        ]
      : []),
  ]);
  // What a combined proposal is applied with: all of its requests.
  const reviewInstruction = refine
    ? combinedNoteEditInstruction([...refine.instructions, instruction])
    : instruction;
  const active = useRef<Lifetime>({ scope, source });
  if (active.current.scope !== scope || active.current.source !== source)
    active.current = { scope, source };
  const lifetime = active.current;
  const mounted = useRef(true),
    pending = useRef<Lifetime | null>(null);
  const [state, setState] = useState<State | null>(null);
  const options = {
    context: { expectedUserUid: scope?.uid },
    retry: false,
    gcTime: 0,
  };
  const proposal = useMutation(
    orpc.createNoteEditProposal.mutationOptions(options),
  );
  const main = useMutation(orpc.updateVisit.mutationOptions(options));
  const note = useMutation(orpc.updateVisitNote.mutationOptions(options));
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const current = (): boolean =>
    mounted.current &&
    active.current === lifetime &&
    !!scope &&
    getVisitScope() === scope;
  const result = state?.lifetime === lifetime ? state.result : null;
  const clear = (): void => {
    if (current()) {
      active.current = { scope, source };
      setState(null);
    }
  };
  // Shows a proposal again (going back from a refinement that failed or was
  // stopped): it belongs to the current instruction and refinement.
  const show = (value: NoteProposal): void => {
    if (current()) setState({ lifetime, result: value, phase: "ready" });
  };
  const propose = async (): Promise<ProposeOutcome | null> => {
    if (!current() || pending.current === lifetime) return null;
    pending.current = lifetime;
    setState({ lifetime, result: null, phase: "proposing" });
    // A lab report's add sends its own choice; other edits the setting.
    const labs = preChartDraft
      ? preChartAdd?.mode === "labs"
        ? preChartAdd.labs
        : preChartLabsFor(useAuthStore.getState().userData?.public_id)
      : undefined;
    try {
      const input = noteEditProposalInputSchema.parse({
        instruction,
        ...(target.notePublicId === null
          ? {}
          : { notePublicId: target.notePublicId }),
        ...(sourceIds.length ? { sourceIds: [...sourceIds] } : {}),
        ...(origin ? { origin } : {}),
        // Pre-chart drafts: which lab values to take (used only there).
        ...(labs ? { preChartLabs: labs } : {}),
        ...(preChartDraft && preChartAdd
          ? {
              preChartAdd: preChartAdd.mode,
              ...(preChartAdd.mode !== "spread" && preChartAdd.heading
                ? { preChartHeading: preChartAdd.heading }
                : {}),
            }
          : {}),
        ...(refine
          ? {
              refine: {
                text: refine.proposal.proposed,
                expectedVersion: refine.proposal.baseVersion,
                instructions: [...refine.instructions],
              },
            }
          : {}),
      });
      // Validate the parent through the same shared response identity constraint.
      noteEditProposalSchema.shape.target.parse(target);
      const value = noteEditProposalSchema.parse(
        await proposal.mutateAsync({
          params: { public_id: target.visitPublicId },
          body: input,
          query: {},
        }),
      );
      if (
        value.target.visitPublicId !== target.visitPublicId ||
        value.target.notePublicId !== target.notePublicId
      )
        throw Error("Unexpected note identity");
      // A refinement compares the combined result with the same saved note.
      if (refine && value.baseVersion !== refine.proposal.baseVersion)
        throw Error("Unexpected note version");
      if (!current()) return null;
      setState({ lifetime, result: value, phase: "ready" });
      return "ready";
    } catch (error) {
      // The saved note changed since the proposal being refined.
      const stale =
        error instanceof ORPCError &&
        error.code === "CONFLICT" &&
        noteEditProposalConflictSchema.safeParse(error.data).success;
      if (stale && scope) void refreshVisitReads(scope);
      if (!current()) return null;
      if (stale) {
        setState(null);
        return "stale";
      }
      setState({ lifetime, result: null, phase: "proposal-error" });
      return "error";
    } finally {
      if (pending.current === lifetime) {
        pending.current = null;
        proposal.reset();
      }
    }
  };
  const apply = async (
    acknowledgeFactChanges: boolean,
    // Resolves once the visit's reads show the saved note (an edit that
    // says "done" in a toast: the toast comes when the text is in the
    // note), up to NOTE_REFRESH_WAIT_MS.
    { waitForNote = false }: { waitForNote?: boolean } = {},
  ): Promise<"saved" | "stale" | "facts" | "uncertain" | "error" | null> => {
    if (
      !current() ||
      !scope ||
      !result ||
      pending.current === lifetime ||
      (result.blocked && !acknowledgeFactChanges)
    )
      return null;
    pending.current = lifetime;
    setState({ lifetime, result, phase: "applying" });
    let attempted = false;
    try {
      const body = visitNoteUpdateSchema.parse({
        result: result.proposed,
        assistantReview: {
          instruction: reviewInstruction,
          baseVersion: result.baseVersion,
          acknowledgeFactChanges,
          // The server re-checks the facts against the same sources.
          ...(sourceIds.length ? { sourceIds: [...sourceIds] } : {}),
          ...(origin ? { origin } : {}),
        },
      });
      attempted = true;
      const value = savedSchema.parse(
        await (target.notePublicId === null
          ? main.mutateAsync({
              params: { public_id: target.visitPublicId },
              body,
            })
          : note.mutateAsync({
              params: {
                public_id: target.visitPublicId,
                notePublicId: String(target.notePublicId),
              },
              body,
            })),
      );
      if (value.public_id !== (target.notePublicId ?? target.visitPublicId))
        throw Error("Unexpected saved note identity");
      const refreshed = refreshVisitReads(scope);
      if (waitForNote)
        await Promise.race([
          Promise.resolve(refreshed).catch(() => undefined),
          new Promise((resolve) => setTimeout(resolve, NOTE_REFRESH_WAIT_MS)),
        ]);
      if (!current()) return null;
      setState(null);
      return "saved";
    } catch (error) {
      const conflict =
        error instanceof ORPCError && error.code === "CONFLICT"
          ? assistedNoteConflictSchema.safeParse(error.data)
          : null;
      if (
        conflict?.success &&
        conflict.data.reason === "unconfirmed_fact_changes"
      ) {
        if (!current()) return null;
        setState({
          lifetime,
          result: {
            ...result,
            blocked: true,
            factChanges: conflict.data.factChanges,
          },
          phase: "ready",
        });
        return "facts";
      }
      const stale =
        conflict?.success && conflict.data.reason === "stale_proposal";
      const rejected =
        error instanceof ORPCError &&
        [
          "BAD_REQUEST",
          "UNAUTHORIZED",
          "ACCOUNT_UNAVAILABLE",
          "FORBIDDEN",
          "NOT_FOUND",
          "METHOD_NOT_ALLOWED",
          "CONFLICT",
        ].includes(error.code);
      const uncertain = attempted && !rejected;
      if (stale || uncertain) void refreshVisitReads(scope);
      if (!current()) return null;
      if (
        stale ||
        uncertain ||
        (error instanceof ORPCError && error.code === "CONFLICT")
      )
        setState(null);
      else setState({ lifetime, result, phase: "ready" });
      return stale ? "stale" : uncertain ? "uncertain" : "error";
    } finally {
      if (pending.current === lifetime) {
        pending.current = null;
        main.reset();
        note.reset();
      }
    }
  };
  return {
    result,
    propose,
    apply,
    clear,
    show,
    isCurrent: current,
    proposing: state?.lifetime === lifetime && state.phase === "proposing",
    applying: state?.lifetime === lifetime && state.phase === "applying",
    proposeFailed:
      state?.lifetime === lifetime && state.phase === "proposal-error",
  };
}
